import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { bizWorkspaceAPI as bizAPI } from '../services/bizApi';
import { useTheme } from '../context/ThemeContext';
import { ArrowLeft, ShieldCheck, ArrowSquareOut, CheckCircle, Clock } from '@phosphor-icons/react';

const BEBAS = { fontFamily: "'Bebas Neue', sans-serif" };

export const BizShell = ({ title, subtitle, children, testId }) => {
  const { t } = useTheme();
  return (
    <div
      className="min-h-screen pb-16"
      style={{ fontFamily: "'Barlow', sans-serif", background: t.pageBg, color: t.textPrimary }}
      data-testid={testId}
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
        <Link
          to="/workspace"
          className="flex items-center gap-2 text-xs mb-6 transition-colors hover:opacity-80"
          style={{ color: t.textMutedNew }}
        >
          <ArrowLeft className="w-4 h-4" /> Workspace
        </Link>
        <p className="text-[11px] tracking-[0.18em] uppercase font-semibold mb-2" style={{ color: t.textFaint }}>B2B</p>
        <h1 className="text-4xl sm:text-5xl mb-1.5" style={{ ...BEBAS, color: t.textPrimary }}>{title}</h1>
        {subtitle && <p className="text-sm mb-8" style={{ color: t.textMutedNew }}>{subtitle}</p>}
        {children}
      </div>
    </div>
  );
};

/**
 * Theme-aware biz styling helper. Consumed by BizInvoicesPage/BizSplitsPage/BizPledgesPage.
 */
export const useBizStyles = () => {
  const { t } = useTheme();
  return {
    GREEN: t.accent,
    cardStyle: { background: t.surface, border: `1px solid ${t.borderSubtle}`, borderRadius: '1rem' },
    inputStyle: {
      background: t.inputBgNew,
      border: `1px solid ${t.inputBorderNew}`,
      color: t.textPrimary,
    },
    listItemBorder: t.dividerBorder,
    labelColor: t.accentText,
    textPrimary: t.textPrimary,
    textMuted: t.textMutedNew,
    textFaint: t.textFaint,
    hoverBg: t.surfaceHover,
    t,
  };
};

export default function BizSetupPage() {
  const { t } = useTheme();
  const bs = useBizStyles();
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    try { setStatus((await bizAPI.connectStatus()).data); setErr(null); }
    catch (e) { setErr(e?.response?.data?.detail || 'Failed to load'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const onboard = async () => {
    setBusy(true);
    try {
      const { data } = await bizAPI.connectOnboard(window.location.origin + '/biz/setup');
      window.location.href = data.onboarding_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Onboarding unavailable');
      setBusy(false);
    }
  };

  return (
    <BizShell title="Payments Setup" subtitle="Stripe-hosted verification — Divispend never sees your identity documents or holds your funds." testId="biz-setup-page">
      {err && <div className="p-4 text-sm text-[#F59E0B]" style={bs.cardStyle} data-testid="setup-error">{err}</div>}
      {status && (
        <div className="p-6" style={bs.cardStyle} data-testid="connect-status-card">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: t.accentSoft }}>
              <ShieldCheck className="w-5 h-5" style={{ color: bs.GREEN }} weight="bold" />
            </div>
            <div>
              <p className="font-semibold" style={{ color: t.textPrimary }}>Stripe Connect</p>
              <p className="text-xs mt-0.5" style={{ color: t.textMutedNew }}>{status.onboarding_started ? `Account ${status.stripe_account_id}` : 'Not started yet'}</p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
            {[['Details submitted', status.details_submitted], ['Charges enabled', status.charges_enabled], ['Payouts enabled', status.payouts_enabled]].map(([l, v]) => (
              <div key={l} className="rounded-xl p-3 flex items-center gap-2" style={{ background: t.surfaceAlt, border: `1px solid ${t.dividerBorder}` }}>
                {v ? <CheckCircle className="w-4 h-4" style={{ color: bs.GREEN }} weight="fill" /> : <Clock className="w-4 h-4" style={{ color: t.textFaint }} />}
                <span className="text-xs" style={{ color: t.textPrimary }}>{l}</span>
              </div>
            ))}
          </div>
          {status.requirements_currently_due?.length > 0 && (
            <p className="text-[11px] text-[#F59E0B] mb-4">Stripe still needs: {status.requirements_currently_due.join(', ')}</p>
          )}
          {!status.charges_enabled && (
            <button onClick={onboard} disabled={busy} className="h-11 px-5 rounded-xl font-bold text-black text-sm flex items-center gap-2 disabled:opacity-60 hover:opacity-90 transition-opacity" style={{ background: bs.GREEN }} data-testid="start-onboarding-button">
              {status.onboarding_started ? 'Continue verification' : 'Start Stripe verification'} <ArrowSquareOut className="w-4 h-4" />
            </button>
          )}
        </div>
      )}
      <p className="text-[10px] mt-4" style={{ color: t.textFaint }}>Divispend is a software dashboard layer. All fund custody, KYC/identity verification, and money transmission are performed by Stripe, Inc.</p>
    </BizShell>
  );
}
